import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Upload, Trash2, Plus, FileText, Image as ImageIcon, Video, Music,
  Download, Copy, Eye, Save, X, RefreshCcw, Sparkles, Paperclip,
  Newspaper, Star, EyeOff, ExternalLink,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const SUBTABS = [
  { id: "contacts", label: "Contactos", icon: <Upload size={14} /> },
  { id: "templates", label: "Plantillas", icon: <FileText size={14} /> },
  { id: "media", label: "Media", icon: <ImageIcon size={14} /> },
  { id: "coverage", label: "En los medios", icon: <Newspaper size={14} /> },
  { id: "export", label: "Exportar a Resend", icon: <Download size={14} /> },
];

// ==================== Contactos ====================
const ContactsTab = ({ adminKey }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ email: "", medio: "", nombre: "" });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/press/contacts`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setContacts(data);
    } catch (e) {
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    try {
      await axios.post(`${API}/admin/press/contacts`, form, {
        headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
      });
      toast.success("Contacto agregado");
      setForm({ email: "", medio: "", nombre: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar contacto?")) return;
    await axios.delete(`${API}/admin/press/contacts/${id}`, {
      headers: { "X-Admin-Key": adminKey },
    });
    load();
  };

  const wipeAll = async () => {
    if (!window.confirm(`¿Eliminar TODOS los ${contacts.length} contactos? No se puede deshacer.`)) return;
    await axios.delete(`${API}/admin/press/contacts`, {
      headers: { "X-Admin-Key": adminKey },
    });
    toast.success("Contactos eliminados");
    load();
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/press/contacts/upload`, fd, {
        headers: { "X-Admin-Key": adminKey },
      });
      toast.success(`+${data.inserted} contactos · ${data.skipped_duplicates} duplicados · ${data.invalid} inválidos`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload CSV */}
      <div className="bg-[#0A192F] text-white p-6 border border-[#F5B400]/30" data-testid="press-upload-panel">
        <div className="flex items-center gap-3 mb-3">
          <Upload size={18} className="text-[#F5B400]" />
          <h3 className="font-headings uppercase tracking-[0.2em] text-sm text-[#F5B400]">Subir planilla</h3>
        </div>
        <p className="text-xs text-white/60 mb-4">
          Formato: <b>CSV</b> o <b>Excel (.xlsx)</b> con columnas mínimas:
          <span className="text-[#F5B400] mx-1">email</span>,
          <span className="text-[#F5B400] mx-1">medio</span>,
          <span className="text-white/40 mx-1">nombre</span> (opcional).
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls,.tsv"
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
            disabled={uploading}
            className="text-xs text-white/80 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#F5B400] file:text-[#0A192F] file:font-headings file:uppercase file:tracking-wide file:cursor-pointer hover:file:bg-[#d99f00]"
            data-testid="press-upload-input"
          />
          {uploading && <span className="text-xs text-[#F5B400]">Subiendo…</span>}
        </div>
      </div>

      {/* Add one manually */}
      <form onSubmit={submit} className="bg-white p-6 border border-gray-200 grid sm:grid-cols-4 gap-3" data-testid="press-add-form">
        <input
          type="email"
          placeholder="email@medio.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="border border-gray-300 px-3 py-2 text-sm"
          required
          data-testid="press-add-email"
        />
        <input
          type="text"
          placeholder="Medio"
          value={form.medio}
          onChange={(e) => setForm({ ...form, medio: e.target.value })}
          className="border border-gray-300 px-3 py-2 text-sm"
        />
        <input
          type="text"
          placeholder="Nombre (opcional)"
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
          className="border border-gray-300 px-3 py-2 text-sm"
        />
        <button type="submit" className="bg-[#0A2463] hover:bg-[#0A192F] text-white font-headings uppercase text-xs tracking-wide inline-flex items-center justify-center gap-2" data-testid="press-add-btn">
          <Plus size={14} /> Agregar
        </button>
      </form>

      {/* List */}
      <div className="bg-white border border-gray-200" data-testid="press-contacts-table">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="font-headings uppercase text-sm tracking-wide text-[#0A192F]">
            {contacts.length} contactos
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="text-xs uppercase tracking-wide font-headings text-[#0A2463] hover:text-[#0A192F] inline-flex items-center gap-1">
              <RefreshCcw size={12} /> Recargar
            </button>
            {contacts.length > 0 && (
              <button onClick={wipeAll} className="text-xs uppercase tracking-wide font-headings text-red-600 hover:text-red-800 inline-flex items-center gap-1" data-testid="press-wipe-all">
                <Trash2 size={12} /> Borrar todos
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[500px]">
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-500">Cargando…</div>
          ) : contacts.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              Todavía no cargaste contactos. Subí una planilla o agregá uno manualmente.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-600 tracking-wide">
                <tr>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Medio</th>
                  <th className="text-left p-3">Nombre</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="p-3 tabular-nums">{c.email}</td>
                    <td className="p-3">{c.medio || "—"}</td>
                    <td className="p-3">{c.nombre || "—"}</td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => remove(c.id)}
                        className="text-red-500 hover:text-red-700"
                        aria-label="Eliminar"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Plantillas ====================
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["blockquote", "link"],
    ["clean"],
  ],
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "color", "background", "align",
  "blockquote", "link", "image",
];

const InsertMediaModal = ({ open, onClose, onPick, adminKey }) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/press/media`, {
      headers: { "X-Admin-Key": adminKey },
    }).then(({ data }) => setItems(data)).catch(() => {});
  }, [open, adminKey]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="press-media-modal">
      <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-headings uppercase text-sm tracking-[0.2em] text-[#0A192F]">Elegí un archivo para insertar</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={18} /></button>
        </div>
        <div className="p-4">
          {items.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-10">
              No hay media todavía. Subí archivos desde <b>Prensa → Media</b>.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => onPick(it)}
                  className="text-left bg-white border border-gray-200 hover:border-[#F5B400] p-2 transition-colors"
                  data-testid={`press-modal-pick-${it.id}`}
                >
                  {it.tipo === "image" ? (
                    <img src={`${BACKEND}${it.url_path}`} alt={it.nombre_original} className="w-full h-24 object-cover" />
                  ) : (
                    <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-gray-400">
                      {it.tipo === "audio" ? <Music size={22} /> : <Video size={22} />}
                      <span className="ml-2 uppercase text-[10px] font-headings tracking-wide">{it.tipo}</span>
                    </div>
                  )}
                  <div className="text-xs mt-2 truncate">{it.nombre_original}</div>
                  <div className="text-[10px] text-gray-500">{it.size_mb} MB · {it.tipo}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TemplatesTab = ({ adminKey }) => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nombre: "", asunto: "", cuerpo_html: "" });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [previewContact, setPreviewContact] = useState(null);
  const [mediaModal, setMediaModal] = useState(false);
  const quillRef = useRef(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/press/templates`, {
      headers: { "X-Admin-Key": adminKey },
    });
    setTemplates(data);
    const { data: contacts } = await axios.get(`${API}/admin/press/contacts`, {
      headers: { "X-Admin-Key": adminKey },
    });
    if (contacts[0]) setPreviewContact(contacts[0]);
  };
  useEffect(() => { load(); }, []);

  const seedTemplates = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/press/templates/seed`, {}, {
        headers: { "X-Admin-Key": adminKey },
      });
      toast.success(`+${data.created} plantillas base cargadas`);
      load();
    } catch (e) {
      toast.error("Error al cargar plantillas base");
    } finally {
      setSeeding(false);
    }
  };

  const pick = (t) => {
    setSelected(t);
    setForm({ nombre: t.nombre, asunto: t.asunto, cuerpo_html: t.cuerpo_html });
  };

  const newTemplate = () => {
    setSelected(null);
    setForm({
      nombre: "Nueva gacetilla",
      asunto: "Frente Ya · {{medio}}",
      cuerpo_html: `<p>Estimados de <strong>{{medio}}</strong>:</p><p>Les acercamos la gacetilla de Frente Ya…</p><p>Saludos cordiales,<br/><strong>Prensa · Frente Ya</strong></p>`,
    });
  };

  const save = async () => {
    if (!form.nombre.trim() || !form.asunto.trim()) {
      toast.error("Falta nombre o asunto");
      return;
    }
    setSaving(true);
    try {
      if (selected) {
        await axios.patch(`${API}/admin/press/templates/${selected.id}`, form, {
          headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
        });
        toast.success("Plantilla actualizada");
      } else {
        const { data } = await axios.post(`${API}/admin/press/templates`, form, {
          headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
        });
        setSelected(data);
        toast.success("Plantilla creada");
      }
      load();
    } catch (e) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar plantilla?")) return;
    await axios.delete(`${API}/admin/press/templates/${id}`, {
      headers: { "X-Admin-Key": adminKey },
    });
    if (selected?.id === id) {
      setSelected(null);
      setForm({ nombre: "", asunto: "", cuerpo_html: "" });
    }
    load();
  };

  const render = (text) => {
    if (!previewContact) return text;
    return text
      .replace(/\{\{\s*email\s*\}\}/g, previewContact.email || "")
      .replace(/\{\{\s*medio\s*\}\}/g, previewContact.medio || "")
      .replace(/\{\{\s*nombre\s*\}\}/g, previewContact.nombre || "");
  };

  // Insertar en el editor Quill (en la posición actual del cursor)
  const insertIntoEditor = (html) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) {
      // Fallback: append
      setForm((f) => ({ ...f, cuerpo_html: (f.cuerpo_html || "") + html }));
      return;
    }
    const range = editor.getSelection(true);
    editor.clipboard.dangerouslyPasteHTML(range?.index ?? editor.getLength(), html);
  };

  const insertPlaceholder = (key) => {
    insertIntoEditor(`{{${key}}}`);
  };

  const insertMedia = (item) => {
    const url = `${BACKEND}${item.url_path}`;
    let html;
    if (item.tipo === "image") {
      html = `<p><img src="${url}" alt="${item.nombre_original}" style="max-width:100%;height:auto"/></p>`;
    } else if (item.tipo === "audio") {
      html = `<p>🎧 <a href="${url}" target="_blank" rel="noopener">Escuchar audio: ${item.nombre_original}</a></p>`;
    } else {
      html = `<p>🎬 <a href="${url}" target="_blank" rel="noopener">Ver video: ${item.nombre_original}</a></p>`;
    }
    insertIntoEditor(html);
    setMediaModal(false);
    toast.success(`"${item.nombre_original}" insertado`);
  };

  return (
    <div className="grid lg:grid-cols-[280px,1fr] gap-6">
      <InsertMediaModal open={mediaModal} onClose={() => setMediaModal(false)} onPick={insertMedia} adminKey={adminKey} />

      {/* Sidebar list */}
      <div className="bg-white border border-gray-200">
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <span className="font-headings uppercase text-xs tracking-wide text-[#0A192F]">Plantillas</span>
          <button onClick={newTemplate} className="text-[#0A2463] hover:text-[#0A192F]" aria-label="Nueva" data-testid="press-tpl-new">
            <Plus size={16} />
          </button>
        </div>
        {templates.length === 0 && (
          <div className="p-3 border-b border-gray-200 bg-yellow-50">
            <p className="text-[11px] text-[#0A192F] mb-2">
              ¿Primera vez? Cargá 4 plantillas listas para usar (lanzamiento, presentación, convocatoria y comunicado).
            </p>
            <button
              onClick={seedTemplates}
              disabled={seeding}
              className="w-full bg-[#F5B400] hover:bg-[#d99f00] text-[#0A192F] font-headings uppercase text-[11px] tracking-wide py-2 inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="press-tpl-seed"
            >
              <Sparkles size={12} /> {seeding ? "Cargando…" : "Cargar plantillas base"}
            </button>
          </div>
        )}
        <div className="max-h-[600px] overflow-y-auto">
          {templates.length === 0 ? (
            <div className="p-4 text-xs text-gray-500">No hay plantillas todavía.</div>
          ) : (
            templates.map((t) => (
              <div
                key={t.id}
                onClick={() => pick(t)}
                className={`p-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 flex items-center justify-between group ${selected?.id === t.id ? "bg-[#F5B400]/15" : ""}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.nombre}</div>
                  <div className="text-[10px] text-gray-500 truncate">{t.asunto}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(t.id); }}
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 ml-2"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="bg-white border border-gray-200 p-5" data-testid="press-tpl-editor">
        {!selected && !form.nombre ? (
          <div className="text-center py-16 text-gray-500">
            <FileText size={32} className="mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Elegí una plantilla o creá una nueva.</p>
          </div>
        ) : (
          <>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-headings text-gray-500 mb-1">Nombre interno</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full border border-gray-300 px-3 py-2 text-sm mb-4"
              data-testid="press-tpl-name"
            />
            <label className="block text-[10px] uppercase tracking-[0.2em] font-headings text-gray-500 mb-1">Asunto (podés usar {"{{medio}}"})</label>
            <input
              type="text"
              value={form.asunto}
              onChange={(e) => setForm({ ...form, asunto: e.target.value })}
              className="w-full border border-gray-300 px-3 py-2 text-sm mb-4"
              data-testid="press-tpl-subject"
            />

            <label className="block text-[10px] uppercase tracking-[0.2em] font-headings text-gray-500 mb-2">Cuerpo</label>

            {/* Botones extra sobre la toolbar de Quill */}
            <div className="flex flex-wrap gap-2 mb-2" data-testid="press-tpl-extras">
              <button
                type="button"
                onClick={() => setMediaModal(true)}
                className="border border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463] hover:text-white text-[11px] uppercase tracking-wide font-headings px-3 py-1.5 inline-flex items-center gap-1.5"
                data-testid="press-tpl-insert-media"
              >
                <Paperclip size={12} /> Insertar media
              </button>
              <div className="border-l border-gray-300 mx-1"></div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-headings self-center">Placeholders:</span>
              {["medio", "email", "nombre"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => insertPlaceholder(k)}
                  className="border border-yellow-400 bg-yellow-50 text-[#0A192F] hover:bg-yellow-100 text-[11px] font-mono px-2 py-1.5 inline-flex items-center gap-1"
                  data-testid={`press-tpl-ph-${k}`}
                >
                  <Sparkles size={11} className="text-[#F5B400]" /> {`{{${k}}}`}
                </button>
              ))}
            </div>

            <div className="mb-4 press-quill-wrap" data-testid="press-tpl-body-editor">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={form.cuerpo_html}
                onChange={(html) => setForm((f) => ({ ...f, cuerpo_html: html }))}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Escribí el cuerpo del mail…"
              />
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={save}
                disabled={saving}
                className="bg-[#0A2463] hover:bg-[#0A192F] text-white font-headings uppercase text-xs tracking-wide px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="press-tpl-save"
              >
                <Save size={14} /> {saving ? "Guardando…" : selected ? "Guardar cambios" : "Crear plantilla"}
              </button>
            </div>

            {/* Preview */}
            <div className="border-t border-gray-200 pt-5 mt-5">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={14} className="text-[#F5B400]" />
                <span className="font-headings uppercase text-xs tracking-[0.2em] text-gray-600">
                  Vista previa {previewContact ? `(con ${previewContact.email})` : "(cargá contactos para ver)"}
                </span>
              </div>
              <div className="bg-gray-50 p-4 border border-gray-200">
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Asunto</div>
                <div className="font-medium text-sm mb-3">{render(form.asunto)}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Cuerpo</div>
                <div
                  className="prose prose-sm max-w-none bg-white p-4 border border-gray-200"
                  dangerouslySetInnerHTML={{ __html: render(form.cuerpo_html) }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== Media Library ====================
const MediaTab = ({ adminKey }) => {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/press/media`, {
      headers: { "X-Admin-Key": adminKey },
    });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/press/media/upload`, fd, {
        headers: { "X-Admin-Key": adminKey },
      });
      toast.success(`"${file.name}" subido`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar archivo?")) return;
    await axios.delete(`${API}/admin/press/media/${id}`, {
      headers: { "X-Admin-Key": adminKey },
    });
    load();
  };

  const copyUrl = (item) => {
    const full = `${BACKEND}${item.url_path}`;
    navigator.clipboard.writeText(full);
    toast.success("Link copiado al portapapeles");
  };

  const iconFor = (kind) => {
    if (kind === "image") return <ImageIcon size={18} />;
    if (kind === "audio") return <Music size={18} />;
    if (kind === "video") return <Video size={18} />;
    return <FileText size={18} />;
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#0A192F] text-white p-6 border border-[#F5B400]/30">
        <div className="flex items-center gap-3 mb-3">
          <Upload size={18} className="text-[#F5B400]" />
          <h3 className="font-headings uppercase tracking-[0.2em] text-sm text-[#F5B400]">Subir archivo</h3>
        </div>
        <p className="text-xs text-white/60 mb-4">
          Imágenes (JPG/PNG/WEBP), audio (MP3/WAV/M4A) o video (MP4/MOV/WEBM) · máx <b>50 MB</b> c/u.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,audio/*,video/*"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          disabled={uploading}
          className="text-xs text-white/80 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#F5B400] file:text-[#0A192F] file:font-headings file:uppercase file:tracking-wide file:cursor-pointer hover:file:bg-[#d99f00]"
          data-testid="press-media-input"
        />
        {uploading && <span className="text-xs text-[#F5B400] ml-3">Subiendo…</span>}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? (
          <div className="col-span-full text-center text-sm text-gray-500 py-10 border border-dashed border-gray-300">
            Todavía no subiste archivos.
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="bg-white border border-gray-200 overflow-hidden group" data-testid={`press-media-${it.id}`}>
              {it.tipo === "image" ? (
                <img src={`${BACKEND}${it.url_path}`} alt={it.nombre_original} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-gray-400">
                  {iconFor(it.tipo)}
                  <span className="ml-2 uppercase text-xs font-headings tracking-wide">{it.tipo}</span>
                </div>
              )}
              <div className="p-3">
                <div className="text-xs truncate font-medium" title={it.nombre_original}>{it.nombre_original}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{it.size_mb} MB</div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => copyUrl(it)}
                    className="flex-1 border border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463] hover:text-white text-[10px] uppercase tracking-wide font-headings py-1.5 inline-flex items-center justify-center gap-1"
                    data-testid={`press-media-copy-${it.id}`}
                  >
                    <Copy size={11} /> Copiar URL
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    className="border border-red-400 text-red-500 hover:bg-red-500 hover:text-white text-[10px] py-1.5 px-2 inline-flex items-center"
                    aria-label="Eliminar"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ==================== En los medios (Cobertura) ====================
const emptyCoverage = () => ({
  tipo: "nota",
  titulo: "",
  medio: "",
  fecha: new Date().toISOString().slice(0, 10),
  url: "",
  cover_image_url: "",
  descripcion: "",
  destacado: false,
  oculto: false,
});

const CoverageTab = ({ adminKey }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyCoverage());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/press-coverage`, {
      headers: { "X-Admin-Key": adminKey },
    });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim() || !form.medio.trim() || !form.url.trim()) {
      toast.error("Completá título, medio y URL");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/admin/press-coverage/${editingId}`, form, {
          headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
        });
        toast.success("Actualizado");
      } else {
        await axios.post(`${API}/admin/press-coverage`, form, {
          headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" },
        });
        toast.success("Cobertura agregada");
      }
      setForm(emptyCoverage());
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      tipo: item.tipo,
      titulo: item.titulo,
      medio: item.medio,
      fecha: item.fecha,
      url: item.url,
      cover_image_url: item.cover_image_url || "",
      descripcion: item.descripcion || "",
      destacado: !!item.destacado,
      oculto: !!item.oculto,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cobertura?")) return;
    await axios.delete(`${API}/admin/press-coverage/${id}`, {
      headers: { "X-Admin-Key": adminKey },
    });
    load();
  };

  const toggle = async (item, field) => {
    await axios.patch(`${API}/admin/press-coverage/${item.id}`, {
      ...item, [field]: !item[field],
    }, { headers: { "X-Admin-Key": adminKey, "Content-Type": "application/json" } });
    load();
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyCoverage());
  };

  const badge = (tipo) => {
    const map = {
      nota: { label: "🔗 Nota", bg: "bg-blue-100 text-blue-800" },
      video: { label: "📺 Video", bg: "bg-red-100 text-red-800" },
      pdf: { label: "📄 PDF", bg: "bg-amber-100 text-amber-800" },
    };
    return map[tipo] || map.nota;
  };

  return (
    <div className="space-y-6" data-testid="press-coverage-tab">
      {/* Form */}
      <form onSubmit={submit} className="bg-white p-6 border border-gray-200 space-y-4" data-testid="press-cov-form">
        <div className="flex items-center justify-between">
          <h3 className="font-headings uppercase text-sm tracking-[0.2em] text-[#0A192F]">
            {editingId ? "Editar cobertura" : "Agregar cobertura"}
          </h3>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
              <X size={12} /> Cancelar
            </button>
          )}
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <select
            value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="border border-gray-300 px-3 py-2 text-sm"
            data-testid="press-cov-tipo"
          >
            <option value="nota">🔗 Nota / Link</option>
            <option value="video">📺 Video</option>
            <option value="pdf">📄 PDF</option>
          </select>
          <input
            type="text"
            placeholder="Medio (ej: Clarín)"
            value={form.medio}
            onChange={(e) => setForm({ ...form, medio: e.target.value })}
            className="border border-gray-300 px-3 py-2 text-sm"
            required
            data-testid="press-cov-medio"
          />
          <input
            type="date"
            value={form.fecha}
            onChange={(e) => setForm({ ...form, fecha: e.target.value })}
            className="border border-gray-300 px-3 py-2 text-sm"
            required
          />
          <label className="text-xs inline-flex items-center gap-2 self-center">
            <input
              type="checkbox"
              checked={form.destacado}
              onChange={(e) => setForm({ ...form, destacado: e.target.checked })}
              className="accent-[#F5B400]"
            /> Destacar
          </label>
        </div>
        <input
          type="text"
          placeholder="Título de la nota / entrevista"
          value={form.titulo}
          onChange={(e) => setForm({ ...form, titulo: e.target.value })}
          className="w-full border border-gray-300 px-3 py-2 text-sm"
          required
          data-testid="press-cov-titulo"
        />
        <input
          type="url"
          placeholder={form.tipo === "video" ? "URL de YouTube o video (https://youtu.be/...)" : form.tipo === "pdf" ? "URL del PDF (subilo en Media y pegá el link)" : "URL de la nota (https://clarin.com/...)"}
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          className="w-full border border-gray-300 px-3 py-2 text-sm font-mono"
          required
          data-testid="press-cov-url"
        />
        <input
          type="url"
          placeholder="URL de imagen de portada (opcional, para tipo Nota o Video sin thumbnail)"
          value={form.cover_image_url}
          onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
          className="w-full border border-gray-300 px-3 py-2 text-sm font-mono"
        />
        <textarea
          placeholder="Descripción corta (opcional)"
          value={form.descripcion}
          onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
          rows={2}
          className="w-full border border-gray-300 px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={saving}
          className="bg-[#0A2463] hover:bg-[#0A192F] text-white font-headings uppercase text-xs tracking-wide px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="press-cov-save"
        >
          <Save size={14} /> {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Agregar cobertura"}
        </button>
      </form>

      {/* List */}
      <div className="bg-white border border-gray-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="font-headings uppercase text-sm tracking-wide text-[#0A192F]">
            {items.length} coberturas
          </div>
          <button onClick={load} className="text-xs uppercase tracking-wide font-headings text-[#0A2463] hover:text-[#0A192F] inline-flex items-center gap-1">
            <RefreshCcw size={12} /> Recargar
          </button>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No hay coberturas cargadas todavía.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((it) => {
              const b = badge(it.tipo);
              return (
                <div key={it.id} className={`p-4 flex items-start gap-4 ${it.oculto ? "opacity-50" : ""}`} data-testid={`press-cov-row-${it.id}`}>
                  <span className={`text-[10px] uppercase tracking-wide font-headings px-2 py-1 ${b.bg}`}>{b.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{it.titulo}</span>
                      {it.destacado && <Star size={12} className="text-[#F5B400]" fill="currentColor" />}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {it.medio} · {it.fecha}
                    </div>
                    <a href={it.url} target="_blank" rel="noreferrer" className="text-xs text-[#0A2463] hover:underline inline-flex items-center gap-1 mt-1">
                      <ExternalLink size={11} /> Abrir enlace
                    </a>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => toggle(it, "destacado")} className={`p-1.5 border ${it.destacado ? "border-[#F5B400] text-[#F5B400]" : "border-gray-300 text-gray-400"} hover:text-[#F5B400] hover:border-[#F5B400]`} title="Destacar">
                      <Star size={14} fill={it.destacado ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => toggle(it, "oculto")} className={`p-1.5 border ${it.oculto ? "border-gray-400 bg-gray-100" : "border-gray-300"} text-gray-500 hover:border-gray-500`} title={it.oculto ? "Mostrar" : "Ocultar"}>
                      {it.oculto ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => edit(it)} className="p-1.5 border border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463] hover:text-white" title="Editar">
                      <FileText size={14} />
                    </button>
                    <button onClick={() => remove(it.id)} className="p-1.5 border border-red-400 text-red-500 hover:bg-red-500 hover:text-white" title="Eliminar">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Export ====================
const ExportTab = ({ adminKey }) => {
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState(0);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [rendered, setRendered] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/press/templates`, { headers: { "X-Admin-Key": adminKey } })
      .then(({ data }) => { setTemplates(data); if (data[0]) setSelectedTpl(data[0].id); });
    axios.get(`${API}/admin/press/contacts`, { headers: { "X-Admin-Key": adminKey } })
      .then(({ data }) => setContacts(data.length));
  }, [adminKey]);

  useEffect(() => {
    if (!selectedTpl) { setRendered(null); return; }
    axios.get(`${API}/admin/press/export/template/${selectedTpl}`, {
      headers: { "X-Admin-Key": adminKey },
    }).then(({ data }) => setRendered(data));
  }, [selectedTpl, adminKey]);

  const downloadCSV = async () => {
    const { data } = await axios.get(`${API}/admin/press/export/csv`, {
      headers: { "X-Admin-Key": adminKey },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "frenteya-prensa-contactos.csv";
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("CSV descargado");
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  return (
    <div className="space-y-6">
      <div className="bg-[#F5B400]/10 border-l-4 border-[#F5B400] p-4 text-sm">
        <p className="font-headings uppercase tracking-wide text-[#0A192F] mb-2">Cómo enviar en Resend</p>
        <ol className="list-decimal ml-5 space-y-1 text-xs text-[#0A192F]/80">
          <li>Descargá el <b>CSV</b> de contactos y subilo a <a className="underline" href="https://resend.com/audiences" target="_blank" rel="noreferrer">Resend → Audiences</a>.</li>
          <li>Copiá el <b>HTML del asunto y el cuerpo</b> (formato Resend/Mustache).</li>
          <li>En Resend andá a <b>Broadcasts → New</b>, pegá el asunto y el HTML, elegí la audiencia y presioná <b>Send</b>.</li>
          <li>Los <b>adjuntos de audio/video</b>: no los adjuntes al mail. Pegá los <b>links copiados desde Media</b> dentro del HTML.</li>
        </ol>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-gray-200 p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-headings text-gray-500 mb-3">Paso 1 · Audiencia</div>
          <div className="text-3xl font-headings font-bold text-[#0A192F] mb-1 tabular-nums">{contacts}</div>
          <div className="text-xs text-gray-500 mb-4">contactos totales</div>
          <button
            onClick={downloadCSV}
            disabled={contacts === 0}
            className="w-full bg-[#0A2463] hover:bg-[#0A192F] disabled:opacity-50 text-white font-headings uppercase tracking-wide text-sm px-5 py-3 inline-flex items-center justify-center gap-2"
            data-testid="press-export-csv"
          >
            <Download size={14} /> Descargar CSV para Resend
          </button>
        </div>

        <div className="bg-white border border-gray-200 p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-headings text-gray-500 mb-3">Paso 2 · Plantilla</div>
          <select
            value={selectedTpl}
            onChange={(e) => setSelectedTpl(e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 text-sm mb-4"
            data-testid="press-export-tpl-select"
          >
            <option value="">— Elegí una plantilla —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
          {rendered && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-headings">Asunto</span>
                  <button onClick={() => copy(rendered.asunto, "Asunto")} className="text-[10px] uppercase font-headings text-[#0A2463] hover:text-[#0A192F] inline-flex items-center gap-1">
                    <Copy size={11} /> Copiar
                  </button>
                </div>
                <input readOnly value={rendered.asunto} className="w-full border border-gray-300 px-2 py-1.5 text-xs bg-gray-50" data-testid="press-export-subject" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-headings">HTML del cuerpo</span>
                  <button onClick={() => copy(rendered.cuerpo_html, "HTML")} className="text-[10px] uppercase font-headings text-[#0A2463] hover:text-[#0A192F] inline-flex items-center gap-1">
                    <Copy size={11} /> Copiar
                  </button>
                </div>
                <textarea readOnly value={rendered.cuerpo_html} rows={8} className="w-full border border-gray-300 px-2 py-1.5 text-[10px] font-mono bg-gray-50" data-testid="press-export-html" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== Main ====================
export const PressManager = ({ adminKey }) => {
  const [sub, setSub] = useState("contacts");

  return (
    <div className="space-y-6" data-testid="press-manager">
      <div>
        <h2 className="font-headings uppercase text-2xl tracking-tight text-[#0A192F] mb-2">
          Prensa · Gacetillas
        </h2>
        <p className="text-sm text-gray-600 max-w-2xl">
          Administrá contactos, plantillas y media. Después exportás todo a Resend para el envío real (con tu dominio verificado).
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-300 -mb-2">
        {SUBTABS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`px-4 py-2.5 font-headings uppercase tracking-wide text-xs border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${
              sub === s.id
                ? "border-[#F5B400] text-[#0A192F]"
                : "border-transparent text-gray-500 hover:text-[#0A192F]"
            }`}
            data-testid={`press-subtab-${s.id}`}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {sub === "contacts" && <ContactsTab adminKey={adminKey} />}
      {sub === "templates" && <TemplatesTab adminKey={adminKey} />}
      {sub === "media" && <MediaTab adminKey={adminKey} />}
      {sub === "coverage" && <CoverageTab adminKey={adminKey} />}
      {sub === "export" && <ExportTab adminKey={adminKey} />}
    </div>
  );
};

export default PressManager;
